import { RawMaterial_AllStocksTourSteps } from "../../components/TourGuide/tourGuideData";
import { useEffect, useState } from "react";
import { rawMaterials, stockAPI } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import { RegistryHeader } from "../../common/Shared";
import { TourGuide } from "../../components/TourGuide/TourGuide";
import { Info } from "lucide-react";
import { FiClock } from "react-icons/fi";
import Model from "../../common/Model";
import dayjs from "dayjs";

const AllStocks = () => {
  const [loading, setLoading] = useState(false);
  const [grouped, setGrouped] = useState({});
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isLedgerOpen, setIsLedgerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const handleOpenLedger = async (row) => {
    setSelectedRow(row);
    setIsLedgerOpen(true);
    setLedgerLoading(true);
    setLedgerData([]);
    try {
      const idVal = row.materialId ?? row.id ?? row.material_Id ?? 0;
      const payload = {
        materialId: Number(idVal),
        startDate: null,
        endDate: null,
        pageNumber: 1,
        pageSize: 100
      };
      const res = await stockAPI.getLedger(payload);
      const items = res?.items || res?.data || res || [];

      const normalized = items.map((item) => {
        const findVal = (obj, keys) => {
          const foundKey = Object.keys(obj).find((k) =>
            keys.some((pk) => pk.toLowerCase() === k.toLowerCase())
          );
          return foundKey !== undefined ? obj[foundKey] : undefined;
        };

        return {
          date: findVal(item, ["txDate", "transactionDate", "date", "createdDate", "createdAt", "dateTime", "transDate"]),
          description: findVal(item, ["txType", "description", "narration", "remarks", "type", "transactionType", "particulars"]),
          reference: findVal(item, ["referenceNo", "refNo", "reference", "ref", "inwardNo", "outwardNo", "batchNo", "documentNo"]),
          in: Number(findVal(item, ["qtyIn", "qty_in", "inwardQty", "inward_qty", "inQty", "in_qty", "inward", "in", "qty_In", "quantityIn", "quantity_In"]) || 0),
          out: Number(findVal(item, ["qtyOut", "qty_out", "outwardQty", "outward_qty", "outQty", "out_qty", "outward", "out", "qty_Out", "quantityOut", "quantity_Out"]) || 0),
          balance: Number(findVal(item, ["balance", "closingBalance", "runningBalance", "currentBalance", "bal", "qty", "closing_Qty", "closingQty"]) || 0),
          unit: findVal(item, ["unit", "unitName", "unit_Name"]) || row.unit || "Kg"
        };
      });

      setLedgerData(normalized);
    } catch (err) {
      notify("Failed to fetch stock ledger", "error");
    } finally {
      setLedgerLoading(false);
    }
  };

  const totalInward = ledgerData.reduce((sum, item) => sum + (item.in || 0), 0);
  const totalOutward = ledgerData.reduce((sum, item) => sum + (item.out || 0), 0);
  const currentBalance = ledgerData.length > 0 ? (ledgerData[ledgerData.length - 1].balance || 0) : 0;

  const handleTourComplete = () => {
    localStorage.setItem("aluex_allstocks_tour_seen", "true");
  };

  const normalizeStock = (data) => {
    const result = [];
    data?.forEach((categoryObj) => {
      Object.keys(categoryObj).forEach((category) => {
        const rows = categoryObj[category] || [];
        if (Array.isArray(rows)) {
          rows.forEach((row) => {
            const extractedId = row.material_Id ?? row.materiaL_ID ?? row.materialId ?? row.id ?? Object.entries(row).find(([k]) => k.toLowerCase().replace(/_/g, '') === 'materialid')?.[1];
            result.push({
              id: extractedId,
              materialId: extractedId,
              category,
              sku: row.skU_NO,
              material: row.materiaL_NAME,
              grade: row.grade,
              supplier: row.supplier_Name,
              qty: Number(row.quantity || 0),
              amount: Number(row.amount || 0),
              unit: row.unit_Name,
            });
          });
        }
      });
    });
    return result;
  };

  const groupByCategory = (items) => {
    return items.reduce((acc, item) => {
      (acc[item.category] = acc[item.category] || []).push(item);
      return acc;
    }, {});
  };

  const fetchStock = async () => {
    setLoading(true);
    try {
      const res = await rawMaterials.getAllStock();
      const flat = normalizeStock(res?.data || []);
      setGrouped(groupByCategory(flat));
    } catch (err) {
      notify("Error fetching stock", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStock();
  }, []);

  return (
    <div className="flex flex-col h-full bg-white font-plus overflow-hidden relative">
      {loading && <Loader centered />}
      <RegistryHeader title="Stock Inventory">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsTourOpen(true)}
            className="flex items-center gap-2 px-6 py-2 text-[10px] font-black uppercase tracking-widest text-primary hover:bg-primary/5 transition-colors border border-primary bg-white shrink-0"
            title="Page Guide"
          >
            <Info size={14} />
            Guide
          </button>
        </div>
      </RegistryHeader>

      <div className="px-8 pt-0 pb-8 flex-1 overflow-auto relative">

        {Object.keys(grouped).length > 0 ? (
          <div className="space-y-8 mt-2">
            {Object.keys(grouped).map((cat) => {
              const rows = grouped[cat];
              const totalQty = rows.reduce((t, r) => t + (r.qty || 0), 0);
              const totalAmount = rows.reduce((t, r) => t + Number(r.amount || 0), 0);
              const avgAmount = totalQty > 0 ? totalAmount / totalQty : 0;

              return (
                <div key={cat} className="border border-gray-200  overflow-hidden" data-tour="allstocks-summary">
                  <div className="bg-gray-50/50 px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                      <h3 className="text-[12px] font-black text-gray-800 uppercase tracking-[2px]">
                        {cat}
                      </h3>
                      <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                        {rows.length} Items in this category
                      </p>
                    </div>
                    <div className="flex items-center gap-8 text-right">
                      <div className="flex flex-col">
                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-[2px] mb-1">
                          TOTAL QTY
                        </span>
                        <span className="text-[20px] font-black text-slate-800 leading-none">
                          {totalQty.toLocaleString()} <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">UNIT</span>
                        </span>
                      </div>
                      <div className="w-[1px] h-8 bg-gray-200"></div>
                      <div className="flex flex-col">
                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-[2px] mb-1 text-primary">
                          Avg Rate
                        </span>
                        <span className="text-[20px] font-black text-primary leading-none">
                          {avgAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto" data-tour="allstocks-table">
                    <Table
                      columns={[
                        { key: "sku", label: "SKU", align: "center" },
                        { key: "material", label: "Material Name", align: "center" },
                        { key: "grade", label: "Grade", align: "center" },
                        { key: "supplier", label: "Supplier", align: "center" },
                        {
                          key: "qty",
                          label: "Qty",
                          align: "center",
                          render: (val) => <span className="font-black text-gray-900">{val}</span>,
                        },
                        { key: "unit", label: "Unit", align: "center" },
                        {
                          key: "ledger",
                          label: "Ledger",
                          align: "center",
                          render: (val, rRow) => (
                            <button
                              onClick={() => handleOpenLedger(rRow)}
                              className="text-primary hover:text-[#2c5ebc] p-1.5 hover:bg-slate-100 rounded-lg transition-all"
                              title="View Audit Trail"
                            >
                              <FiClock size={16} />
                            </button>
                          ),
                        },
                      ]}
                      data={rows}
                      showActions={false}
                      tableClassName="text-[12px] font-bold"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          !loading && (
            <div className="bg-white py-32 text-center">
              <div className="text-gray-200 text-6xl mb-6">📦</div>
              <h3 className="text-[14px] font-black text-gray-400 uppercase tracking-[3px]">
                No Stock Data Found
              </h3>
              <p className="text-[11px] text-gray-300 mt-2 font-bold uppercase tracking-widest">
                Try refreshing the page or check back later.
              </p>
            </div>
          )
        )}
      </div>

      <TourGuide
        isOpen={isTourOpen}
        onClose={() => setIsTourOpen(false)}
        steps={RawMaterial_AllStocksTourSteps}
        onComplete={handleTourComplete}
      />

      <Model
        isOpen={isLedgerOpen}
        onClose={() => setIsLedgerOpen(false)}
        name="Inventory Audit Trail"
        widthClass="max-w-4xl"
      >
        <div className="flex flex-col h-full font-plus text-slate-800">
          <div className="px-7 py-3 bg-slate-50 border-b border-slate-100">
            <span className="text-[10px] font-black text-primary uppercase tracking-[2px]">
              {selectedRow?.material} {selectedRow?.grade ? `(Grade: ${selectedRow.grade})` : ""}
            </span>
          </div>

          {ledgerLoading ? (
            <div className="py-24 text-center">
              <Loader centered />
              <span className="text-xs text-slate-400 font-bold uppercase tracking-wider block mt-4">
                Loading audit logs...
              </span>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-3 border-b border-slate-100">
                <div className="p-5 text-center border-r border-slate-100 bg-white">
                  <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">TOTAL INWARD</span>
                  <span className="text-[18px] font-black text-emerald-600 leading-none">
                    +{totalInward.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="p-5 text-center border-r border-slate-100 bg-white">
                  <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">TOTAL OUTWARD</span>
                  <span className="text-[18px] font-black text-rose-500 leading-none">
                    -{totalOutward.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="p-5 text-center bg-white">
                  <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">CURRENT BALANCE</span>
                  <span className="text-[18px] font-black text-primary leading-none">
                    {currentBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              {/* Transactions Table */}
              <div className="overflow-y-auto max-h-[50vh] p-6 bg-slate-50/50">
                {ledgerData.length > 0 ? (
                  <table className="w-full text-left border-collapse border border-slate-150 rounded-xl overflow-hidden shadow-sm bg-white">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="py-3 px-4 text-[11px] font-black text-slate-400 uppercase tracking-wider text-center w-32">Date</th>
                        <th className="py-3 px-4 text-[11px] font-black text-slate-400 uppercase tracking-wider">Description</th>
                        <th className="py-3 px-4 text-[11px] font-black text-slate-400 uppercase tracking-wider text-right w-28">IN</th>
                        <th className="py-3 px-4 text-[11px] font-black text-slate-400 uppercase tracking-wider text-right w-28">OUT</th>
                        <th className="py-3 px-4 text-[11px] font-black text-slate-400 uppercase tracking-wider text-right w-32">BALANCE</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {ledgerData.map((tx, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                          <td className="py-3 px-4 text-center">
                            <div className="text-[12px] font-black text-slate-700">
                              {tx.date ? dayjs(tx.date).format("DD MMM YYYY") : "-"}
                            </div>
                            <div className="text-[10px] text-slate-400 font-bold mt-0.5">
                              {tx.date ? dayjs(tx.date).format("HH:mm:ss") : ""}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-[12px] font-bold text-slate-800">{tx.description || "-"}</div>
                            <div className="text-[10px] text-slate-400 font-bold mt-0.5">Ref: {tx.reference || "-"}</div>
                          </td>
                          <td className="py-3 px-4 text-right">
                            {tx.in > 0 ? (
                              <span className="text-[13px] font-black text-emerald-600">{tx.in.toFixed(2)}</span>
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {tx.out > 0 ? (
                              <span className="text-[13px] font-black text-rose-500">{tx.out.toFixed(2)} </span>
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className="text-[13px] font-black text-slate-800">{tx.balance.toFixed(2)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="py-16 text-center bg-white rounded-xl border border-slate-200 border-dashed">
                    <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">
                      No transaction records found
                    </span>
                  </div>
                )}
              </div>

              {/* Modal Footer with Verification Shield */}
              <div className="shrink-0 flex items-center justify-between px-7 py-4 bg-slate-50 border-t border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 border border-blue-100 rounded-lg text-blue-600">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[9px] font-black text-slate-800 uppercase tracking-wider">AUDIT CHAIN VERIFIED</span>
                    <span className="text-[9px] font-bold text-slate-400">{ledgerData.length} Transactions Sequenced</span>
                  </div>
                </div>
                <div className="flex flex-col text-right">
                  <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">SYSTEM TIME</span>
                  <span className="text-[10px] font-bold text-slate-500">{dayjs().format("DD/M/YYYY, h:mm:ss a")}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </Model>
    </div>
  );
};

export default AllStocks;
